/**
 * Identity factor leaderboard page.
 *
 * Renders the "<X>coded" ranking for a given identity coin key (unc,
 * normie, etc.). Reads from GET /api/identity-factors/:key and shows
 * the main metric: "<X>s hold N% of this token".
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Header from './Header';
import Footer from './Footer';
import { apiFetch, getApiHost } from '../utils/api';

interface IdentityFactorItem {
  mint: string;
  name: string;
  symbol: string;
  uri?: string;
  uriMetadata?: { image?: string; name?: string; symbol?: string };
  holderCount: number;
  holderSupplyPct: number;
  holderSharePct: number;
  targetHoldersAnalyzed: number;
  watchlistAddedAt?: string;
}

interface IdentityFactorResponse {
  identityKey: string;
  identitySymbol: string;
  identityMint: string;
  identityImage?: string;
  identitySnapshotAt?: string;
  identityTotalHolders?: number;
  items: IdentityFactorItem[];
  filters: {
    limit: number;
    sort: 'supply' | 'count';
    minHolderCount: number;
    minAnalyzed: number;
  };
}

interface Props {
  identityKey: string;
}

const IdentityFactorPage: React.FC<Props> = ({ identityKey }) => {
  const [data, setData] = useState<IdentityFactorResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sort, setSort] = useState<'supply' | 'count'>('supply');

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiFetch(`${getApiHost()}/api/identity-factors/${identityKey}?sort=${sort}&limit=100`);
      if (!res.ok) {
        throw new Error(`Server returned ${res.status}`);
      }
      const json = (await res.json()) as IdentityFactorResponse;
      setData(json);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, [identityKey, sort]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const sym = data?.identitySymbol || identityKey;
  const pageTitle = `${sym}factor — how ${sym} coded is each token`;

  return (
    <div className="min-h-screen bg-crypto-dark text-white">
      <Helmet>
        <title>{pageTitle} | deepnets.ai</title>
      </Helmet>
      <Header />

      <main className="max-w-5xl mx-auto px-4 py-6">
        {/* ── Title block ─────────────────────────────────────────────── */}
        <div className="mb-6 flex items-center gap-4">
          {data?.identityImage && (
            <img
              src={data.identityImage}
              alt={`$${sym}`}
              className="w-20 h-20 rounded-full bg-gray-700 border border-gray-700 object-cover flex-shrink-0"
              onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }}
            />
          )}
          <div className="flex-1 min-w-0">
            <h1 className="text-3xl font-bold mb-1">
              <span className="text-crypto-accent">${sym}</span>factor
            </h1>
            <p className="text-gray-400 text-sm">
              How {sym} coded is each watchlist token — ranked by the percentage of each token's
              supply held by wallets that also hold ${sym}.
            </p>
            {data && (
              <p className="text-gray-500 text-xs mt-1">
                Based on a snapshot of {data.identityTotalHolders?.toLocaleString() || '?'} ${sym} holders
                {data.identitySnapshotAt && (
                  <> taken {new Date(data.identitySnapshotAt).toLocaleString()}</>
                )}
                . Only showing tokens with ≥ {data.filters.minHolderCount} overlapping holders and
                ≥ {data.filters.minAnalyzed} analyzed target holders.
              </p>
            )}
          </div>
        </div>

        {/* ── Sort toggle ─────────────────────────────────────────────── */}
        <div className="flex items-center gap-2 mb-4">
          <span className="text-xs text-gray-500">Sort by:</span>
          <button
            onClick={() => setSort('supply')}
            className={`px-3 py-1.5 text-xs rounded-lg border transition-colors ${
              sort === 'supply'
                ? 'bg-crypto-accent/20 border-crypto-accent text-crypto-accent'
                : 'bg-crypto-gray border-gray-700 text-gray-400 hover:text-white'
            }`}
          >
            Supply %
          </button>
          <button
            onClick={() => setSort('count')}
            className={`px-3 py-1.5 text-xs rounded-lg border transition-colors ${
              sort === 'count'
                ? 'bg-crypto-accent/20 border-crypto-accent text-crypto-accent'
                : 'bg-crypto-gray border-gray-700 text-gray-400 hover:text-white'
            }`}
          >
            Holder Count
          </button>
        </div>

        {/* ── States ──────────────────────────────────────────────────── */}
        {loading && <div className="text-gray-500 text-sm py-8 text-center">Loading…</div>}
        {error && !loading && (
          <div className="text-red-400 text-sm py-8 text-center">
            Failed to load: {error}
          </div>
        )}

        {/* ── Table ───────────────────────────────────────────────────── */}
        {!loading && !error && data && data.items.length > 0 && (
          <div className="bg-crypto-gray border border-gray-700 rounded-xl overflow-hidden">
            <table className="w-full">
              <thead className="bg-crypto-dark border-b border-gray-700">
                <tr>
                  <th className="text-left p-2 sm:p-3 text-xs font-medium text-gray-500 w-8 sm:w-12">#</th>
                  <th className="text-left p-2 sm:p-3 text-xs font-medium text-gray-500">Token</th>
                  <th className="text-right p-2 sm:p-3 text-xs font-medium text-gray-500 whitespace-nowrap">
                    ${sym}s hold
                  </th>
                  <th className="text-right p-2 sm:p-3 text-xs font-medium text-gray-500 whitespace-nowrap hidden sm:table-cell">
                    Overlapping<br/>holders
                  </th>
                  <th className="text-right p-2 sm:p-3 text-xs font-medium text-gray-500 whitespace-nowrap hidden md:table-cell">
                    % of ${sym} holders<br/>in this token
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700/50">
                {data.items.map((item, idx) => {
                  const image = item.uriMetadata?.image;
                  return (
                    <tr key={item.mint} className="hover:bg-crypto-dark/50 transition-colors">
                      <td className="p-2 sm:p-3 text-sm text-gray-500 font-mono">{idx + 1}</td>
                      <td className="p-2 sm:p-3">
                        <Link to={`/token/${item.mint}`} className="flex items-center gap-2 sm:gap-3 group">
                          {image ? (
                            <img
                              src={image}
                              alt=""
                              className="w-8 h-8 rounded-full bg-gray-700 flex-shrink-0"
                              onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }}
                            />
                          ) : (
                            <div className="w-8 h-8 rounded-full bg-gray-700 flex items-center justify-center text-xs text-gray-400 flex-shrink-0">
                              {item.symbol?.charAt(0) || '?'}
                            </div>
                          )}
                          <div className="min-w-0">
                            <div className="text-sm font-medium text-white group-hover:text-crypto-accent transition-colors truncate max-w-[100px] sm:max-w-[200px]">
                              {item.name || 'Unknown'}
                            </div>
                            <div className="text-xs text-gray-500 truncate max-w-[100px] sm:max-w-[200px]">${item.symbol}</div>
                          </div>
                        </Link>
                      </td>
                      <td className="p-2 sm:p-3 text-right whitespace-nowrap">
                        <div className="text-lg font-semibold text-crypto-accent">
                          {item.holderSupplyPct.toFixed(2)}%
                        </div>
                        <div className="text-xs text-gray-500">
                          of {item.targetHoldersAnalyzed} analyzed
                        </div>
                      </td>
                      <td className="p-2 sm:p-3 text-right text-sm text-gray-300 font-mono whitespace-nowrap hidden sm:table-cell">
                        {item.holderCount}
                      </td>
                      <td className="p-2 sm:p-3 text-right text-sm text-gray-400 font-mono whitespace-nowrap hidden md:table-cell">
                        {item.holderSharePct.toFixed(2)}%
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {!loading && !error && data && data.items.length === 0 && (
          <div className="text-gray-500 text-sm py-8 text-center">
            No tokens match the current filters.
          </div>
        )}
      </main>

      <Footer />
    </div>
  );
};

export default IdentityFactorPage;
